import type { RouterOutputs } from '@poseidon/api/types';
import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';
import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import { Development } from '@poseidon/components/StrokeIcons';

import { lockedGraphContentHeight, SequenceGraphEditor } from '~/components/Sequence/SequenceGraphEditor';

type ContactSequenceGraph = RouterOutputs['sequence']['enrollmentGraphsForContact']['enrollments'][number];

/** Human labels for the coarse enrollment status (the same lifecycle the People list shows). */
const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  waiting: 'Waiting',
  blocked: 'Blocked',
  human: 'Needs review',
  errored: 'Errored',
};

const OWNER_TOOLTIP =
  'This contact is worked on behalf of this person: touches send from their LinkedIn account, inside their working hours and daily limits, and their call/reminder to-dos land on their list.';

/**
 * Canvas height for a graph of `stepCount` steps. The locked view is a short spine — one Prep node, the
 * steps, the Finished terminal — so the old flat 70vh box left most of a screen empty under a three-node
 * sequence. It's sized to the rows it will actually draw (from the editor's own geometry, so the two can't
 * drift), still bounded by the viewport so a long sequence can't take over the detail panel, and floored so
 * the smallest graph keeps room to breathe. Past the viewport bound — and for branch arms no row count can
 * predict — pan / "fit to screen" take over, as they always have.
 */
const CANVAS_PADDING = 64; // the canvas opens 32px down; leave the same margin under the last node
const graphHeight = (stepCount: number) =>
  `max(320px, min(${lockedGraphContentHeight(stepCount) + CANVAS_PADDING}px, 72vh))`;

interface Props {
  enrollment: ContactSequenceGraph;
  teamId: string;
  /** `@`-variables + `/`-documents for the per-step content editor (this contact's values). */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  /** Persist a per-contact body/subject override for one step (keyed by sequenceOverrideKey). */
  onSaveStepContent: (overrideKey: string, next: { body?: string; subject?: string }) => Promise<void>;
  /** Mark the call/reminder to-do this contact is parked on done — advances the arm (by manual-task wait node id). */
  onCompleteManualStep: (nodeId: string) => Promise<void>;
}

/**
 * One active sequence enrollment on the contact detail page, drawn as the LOCKED touch-sequence graph — the
 * same node canvas the builder uses, but structure-frozen: delays / order / branches can't change, and
 * clicking a send step edits only its body/subject as a per-contact override (never the template). The
 * graph highlights where this contact currently is and locks steps already sent.
 *
 * Everything the graph doesn't need to repeat lives in this header: the sequence's name, its status, and
 * who it runs as. That last one is a property of the whole sequence rather than a stage of this contact's
 * journey, so it reads as a caption here instead of costing a row on the canvas.
 */
export function ContactSequenceGraphCard({
  enrollment,
  teamId,
  variables,
  documents,
  onSaveStepContent,
  onCompleteManualStep,
}: Props) {
  return (
    <div className="overflow-hidden rounded-xl border border-lynch-200 dark:border-slate-700">
      <div className="flex items-center gap-2 border-b border-lynch-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        <Development.Dataflow04 className="size-4 shrink-0 text-aurora-500" />
        <span className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200">{enrollment.sequenceName}</span>
        <Tooltip tooltip={OWNER_TOOLTIP} tooltipMaxWidth={280} className="flex min-w-0 items-center gap-1.5">
          <TeamUserAvatar
            userTeam={{ name: enrollment.ownerName, profilePicture: enrollment.ownerProfilePicture }}
            shape="circle"
            className="size-5 shrink-0 text-[9px]"
          />
          <span className="truncate text-xs text-lynch-500 dark:text-lynch-400">{enrollment.ownerName ?? 'No owner'}</span>
        </Tooltip>
        <span
          className={cn(
            'ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            enrollment.status === 'errored'
              ? 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300'
              : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',
          )}>
          {STATUS_LABEL[enrollment.status] ?? enrollment.status}
        </span>
      </div>
      {/* The pan/zoom canvas needs a sized parent, so the box is measured from the rows this sequence will
          draw (see graphHeight). The wheel still scrolls the page past it (SequenceGraphEditor turns
          `wheelPan` off in this mode), which is what makes an inline canvas safe in the detail panel. */}
      <div className="relative bg-white dark:bg-stone-900" style={{ height: graphHeight(enrollment.steps.length) }}>
        <SequenceGraphEditor
          readOnly
          teamId={teamId}
          sequenceId={enrollment.sequencePublicId}
          initialSteps={enrollment.steps}
          overrides={enrollment.overrides}
          stepStatus={enrollment.stepStatus}
          currentNodeId={enrollment.currentNodeId}
          onSaveStepContent={onSaveStepContent}
          onCompleteManualStep={onCompleteManualStep}
          variables={variables}
          documents={documents}
        />
      </div>
    </div>
  );
}
